import {
  DataBlockDictionaryColumnDto,
  DataBlockDictionaryItemDto,
  QueryFactory,
} from '@lmt/shared-types';
import { useDictionaryById } from '@lmt/unicorn-shared/app/api/custom-hooks';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  sortingQueryParams,
  toScrollQueryParams,
} from '@lmt/unicorn-shared/app/components/common/table/operations';
import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { SearchInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/search/SearchInput';
import { useInsertKeyHandler } from '@lmt/unicorn-shared/app/components/dictionaries/useInsertKeyHandler';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { isEmpty } from '@lmt/unicorn-shared/app/components/measurements/copy-datablock-field/common';
import tableStyles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import styles from '@lmt/unicorn-shared/app/components/patients/list/PatientListPage.module.scss';
import {
  exportToExcelFile,
  createDictionaryItemObjects,
  createReferencedItemObjects,
  useDictionaryKeyTranslation,
  useInvalidateDependantsHandle,
  checkIfColumnsAreTheSame,
  getUniqueColumnsWithDuplicateValues,
  removeEmptyFirstEntries,
  isColumnRequired,
} from '@lmt/unicorn-shared/app/components/settings/CustomDictionary/helpers';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { removeTab } from '@lmt/unicorn-shared/app/features/navigation/operations';
import {
  readFileAsExcel,
  triggerDownloadFileDialog,
} from '@lmt/unicorn-shared/app/helpers/file-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { localizeDictionaryColumnTitle } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameof } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import SettingsIcon from '@lmt/unicorn-shared/app/icons/gear.svg?react';
import PlusIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import { getState, useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useEventCallback } from '@mui/material';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { groupBy, isEqual } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StringParam, withDefault } from 'react-router-url-params';
import { toast } from 'react-toastify';
import { useDebouncedCallback } from 'use-debounce';

import ArrowDown from '../../../icons/arrow-down.svg?react';
import { TableDotMenu } from '../../common/table/TableDotMenu';
import { useTabId } from '../../keep-alive/TabContext';

import pageStyles from './CustomDictionaryPage.module.scss';
import { useModal } from '../../common/ui-kit/modal/useModal';
import { DataGrid } from '../../common/dataGrid/DataGrid';
import {
  RowClickEvent,
  SortOrder,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';

export const CustomDictionaryPage: React.FC = React.memo(
  function CustomDictionary() {
    const params = pages.customDictionary.useParams();
    const dictionaryId = params.dictionaryId!;
    const i18n = useScopedTranslation('custom_dictionary.list');

    const i18nDictKey = useDictionaryKeyTranslation();
    const dispatch = useUniDispatch();
    const modals = useModal();
    const queryClient = useQueryClient();
    const globalForms = useGlobalForms();
    const tabId = useTabId();
    const navigate = useNavigate();
    const inputFileRef = useRef<HTMLInputElement>(null);
    const claims = useClaims();

    const [isLoading, setIsLoading] = useState(false);

    const [queryParams, setQueryParams] = useQueryParamsInTab({
      search: withDefault(StringParam, undefined),
      ...sortingQueryParams(nameof<DataBlockDictionaryItemDto>('value'), false),
    });

    const [isDictItemsQueryEnabled, setDictItemsQueryEnabled] =
      useState<boolean>(true);
    const dictionaryItemsQuery =
      QueryFactory.DataBlockDictionaryItemQuery.useGetQuery(
        {
          dictionaryId: dictionaryId,
          all: true,
          ...toScrollQueryParams({ ...queryParams }),
        },
        {
          //notifyOnChangeProps: ['data'],
          enabled: isDictItemsQueryEnabled,
          placeholderData: keepPreviousData,
        },
      );

    const currentDictionary = useDictionaryById(dictionaryId);

    const { invalidate: invalidateDependants } =
      useInvalidateDependantsHandle(dictionaryId);

    useEffect(
      function closePageIfDictionaryIsDeleted() {
        if (currentDictionary?.isDeleted) {
          removeTab(tabId!, true, navigate)(dispatch, getState);
        }
      },
      [currentDictionary?.isDeleted, tabId],
    );

    const invalidateQuery = useCallback(async () => {
      await queryClient.invalidateQueries({
        queryKey:
          QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(dictionaryId),
        exact: true,
      });
      await invalidateDependants();
    }, [dictionaryId]);

    const onAddNewItem = useEventCallback(async () => {
      if (currentDictionary?.isReadOnly) {
        return;
      }
      globalForms.openCreateCustomDictionaryItem(dictionaryId);
    });
    useInsertKeyHandler(onAddNewItem);

    useEffect(() => {
      if (currentDictionary?.manualOrdering) {
        onSearchUpdated('');
      }
    }, [currentDictionary?.manualOrdering]);

    const onEdit = useEventCallback(
      async (item: DataBlockDictionaryItemDto) => {
        if (currentDictionary?.isReadOnly) {
          return;
        }
        globalForms.openEditCustomDictionaryItem(dictionaryId, item);
      },
    );

    const onDelete = useEventCallback(
      async (item: DataBlockDictionaryItemDto) => {
        try {
          setIsLoading(true);
          await QueryFactory.DataBlockDictionaryItemClient.remove(item.id);
          await invalidateQuery();
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        } finally {
          setIsLoading(false);
        }
      },
    );

    const onRestore = useEventCallback(
      async (item: DataBlockDictionaryItemDto) => {
        try {
          setIsLoading(true);
          await QueryFactory.DataBlockDictionaryItemClient.patch(item.id, {
            isDeleted: false,
          });
          await invalidateQuery();
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        } finally {
          setIsLoading(false);
        }
      },
    );

    const [referencedData, setReferencedData] = useState<
      Record<string, DataBlockDictionaryItemDto[]>
    >({});

    const data = useMemo(() => {
      const result = (dictionaryItemsQuery.data?.data ?? []).filter(
        (x) => !x.isDeleted,
      );
      return currentDictionary?.manualOrdering
        ? [...result].sort(
            (a, b) => a.manualOrderPosition - b.manualOrderPosition,
          )
        : result;
    }, [
      dictionaryItemsQuery.data,
      referencedData,
      i18n.i18n.language,
      currentDictionary,
    ]);

    const onReorder = useEventCallback(
      async (item: DataBlockDictionaryItemDto, direction: 'up' | 'down') => {
        const isUp = direction == 'up';
        const currentPos = item.manualOrderPosition;
        const otherItem = (isUp ? data.reverse() : data).find((i) =>
          isUp
            ? i.manualOrderPosition < currentPos
            : i.manualOrderPosition > currentPos,
        );
        if (!otherItem) {
          console.error('Tried to swap places with undefined!');
          throw Error('Tried to swap places with undefined!');
        }
        const newPos = otherItem.manualOrderPosition;
        const prom1 = QueryFactory.DataBlockDictionaryItemClient.patch(
          item.id,
          {
            manualOrderPosition: newPos,
          },
        );
        const prom2 = QueryFactory.DataBlockDictionaryItemClient.patch(
          otherItem.id,
          {
            manualOrderPosition: currentPos,
          },
        );
        await Promise.all([prom1, prom2]);
        await invalidateQuery();
      },
    );

    const columns = useMemo(() => {
      const columns = [];

      if (currentDictionary?.manualOrdering) {
        columns.push({
          key: 'manualOrderPosition',
          dataField: 'manualOrderPosition',
          caption: '',
          allowSorting: false,
          defaultSortOrder: 'asc' as SortOrder,
          width: '76px',
          cellRender: (cell: any) => (
            <>
              {cell.rowIndex > 0 ? (
                <IconButton
                  color="grey"
                  iconComponent={
                    <ArrowDown
                      className={clsx(
                        pageStyles.upsideDown,
                        pageStyles.arrowIcon,
                      )}
                    />
                  }
                  className={pageStyles.reorderButton}
                  onClick={() => onReorder(cell.data, 'up')}
                  testId={'reorder-up'}
                />
              ) : null}
              {cell.rowIndex != data.length - 1 ? (
                <IconButton
                  color="grey"
                  iconComponent={<ArrowDown className={pageStyles.arrowIcon} />}
                  className={pageStyles.reorderButton}
                  onClick={() => onReorder(cell.data, 'down')}
                  testId={'reorder-down'}
                />
              ) : null}
            </>
          ),
        });
      }

      columns.push({
        key: 'value',
        dataField: 'value',
        caption: i18n.t('columns.value'),
        allowSorting: !currentDictionary?.manualOrdering,
        defaultSortOrder: !currentDictionary?.manualOrdering
          ? ('asc' as SortOrder)
          : undefined,
        cellRender: (cell: any) => (
          <div
            data-deleted={true}
            className={clsx(tableStyles.valueCell, {
              [tableStyles.deleted]: cell.data.isDeleted,
            })}
            title={cell.data.value}
          >
            {i18nDictKey.t(cell.data.value as any, { nsSeparator: false })}
          </div>
        ),
        calculateCellValue: (row: any) =>
          i18nDictKey.t(row.value as any, { nsSeparator: false }),
      });

      (currentDictionary?.columns ?? [])
        .sort((a, b) =>
          isColumnRequired(a, currentDictionary)
            ? isColumnRequired(b, currentDictionary)
              ? a.title.localeCompare(b.title)
              : -1
            : 1,
        )
        .forEach((column) => {
          const getDisplayedValue = (row: DataBlockDictionaryItemDto) =>
            column.referenceDictionaryId
              ? i18nDictKey.t(
                  referencedData[column.title]?.find(
                    (x) => x.id === row.values[column.title],
                  )?.value as any,
                  {
                    nsSeparator: false,
                  },
                )
              : i18nDictKey.t(row.values[column.title] as any, {
                  nsSeparator: false,
                });

          columns.push({
            key: column.title,
            dataField: column.title,
            caption: localizeDictionaryColumnTitle(
              currentDictionary,
              column.title,
            ),
            dictionaryId: column.referenceDictionaryId,
            isUnique: column.isUnique,
            cellRender: (cell: any) => getDisplayedValue(cell.data),
            calculateCellValue: (row: any) =>
              getDisplayedValue(row).toLocaleLowerCase(),
          });
        });

      columns.push({
        key: 'option',
        caption: '',
        allowSorting: false,
        width: '76px',
        cellRender: (cell: any) => (
          <TableDotMenu
            className={styles.optionsButton}
            options={[
              {
                title: i18n.t('options.edit'),
                onClick: () => onEdit(cell.data),
                isHidden: currentDictionary?.isReadOnly || cell.data.isDeleted,
              },
              {
                title: i18n.t('options.restore'),
                onClick: () => onRestore(cell.data),
                isHidden: currentDictionary?.isReadOnly || !cell.data.isDeleted,
              },
              {
                title: i18n.t('options.delete'),
                onClick: () => onDelete(cell.data),
                isDeleteItem: true,
                isUnderLine: true,
                isHidden: cell.data.isDeleted,
              },
            ]}
          />
        ),
      });
      return columns;
    }, [currentDictionary, data]);

    const onSearchUpdated = useDebouncedCallback((value: string) => {
      setQueryParams({
        search: value,
      });
    }, 300);

    const exportDictionary = useCallback(async () => {
      if (!currentDictionary) {
        return;
      }

      const exportColumns = [
        { header: i18n.t('columns.value'), key: 'value', width: 30 },
        ...currentDictionary.columns.map((x) => {
          return {
            header: localizeDictionaryColumnTitle(currentDictionary, x.title),
            key: x.title,
            width: 30,
          };
        }),
      ];
      const exportData = data.map((item) => {
        return {
          ...item,
          value: i18nDictKey.t(item.value as any, { nsSeparator: false }),
        };
      });
      const result = await exportToExcelFile(
        currentDictionary,
        exportData,
        referencedData,
        exportColumns,
      );
      await triggerDownloadFileDialog(
        new Blob([result]),
        `${i18nDictKey.t(currentDictionary.title as any)}.xlsx`,
      );
    }, [data, currentDictionary, referencedData]);

    const importDictionaryFromFile = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!currentDictionary) {
          return;
        }
        const file = e.target!.files![0];
        if (!file) {
          return;
        }

        try {
          setIsLoading(true);
          // #49024: prevent multiple refetching when importing; refetch only once when import is finished
          setDictItemsQueryEnabled(false);

          const rawValues = await readFileAsExcel(file);
          const values = removeEmptyFirstEntries(rawValues);

          const dataColumns = columns.filter(
            (x) => x.key !== 'manualOrderPosition' && x.key !== 'option',
          );

          if (!checkIfColumnsAreTheSame(values, dataColumns as any)) {
            await modals.showError({
              text: i18n.t('columns_are_not_the_same'),
            });
            return;
          }

          const columnsWithDuplicates = getUniqueColumnsWithDuplicateValues(
            values,
            dataColumns as any,
          );
          if (columnsWithDuplicates.length) {
            await modals.showError({
              text: i18n.t('unique_columns_have_duplicate_values', {
                columns: columnsWithDuplicates.join(', '),
              }),
            });
            return;
          }

          const referencedItems = await createReferencedItemObjects(
            values,
            dataColumns as any,
            referencedData,
          );
          const createdReferencedData: Record<
            string,
            DataBlockDictionaryItemDto[]
          > = {};
          for (const dictKey in referencedItems) {
            const createdItems: DataBlockDictionaryItemDto[] = [];
            const dictItems = referencedItems[dictKey];
            for (const item of dictItems) {
              try {
                const createdItem =
                  await QueryFactory.DataBlockDictionaryItemClient.create(item);
                createdItems.push(createdItem);
              } catch (e) {
                await modals.showError({
                  text: i18n.t('error_when_creating_referenced_item', {
                    value: item.value,
                    column: dictKey,
                    error: convertToErrorString(e),
                  }),
                });
                return;
              }
            }
            createdReferencedData[dictKey] = createdItems;
          }
          const allReferencedData: Record<
            string,
            DataBlockDictionaryItemDto[]
          > = {};
          for (const key in referencedData) {
            allReferencedData[key] = [
              ...referencedData[key],
              ...(createdReferencedData[key] ?? []),
            ];
          }

          const dictionaryItems = await createDictionaryItemObjects(
            currentDictionary.id,
            values,
            dataColumns as any,
            allReferencedData,
          );
          if (isEmpty(dictionaryItems)) {
            toast(i18n.t('no_data_to_import_toast'), {
              type: 'warning',
            });
            return;
          }

          const duplicateValues: Record<string, number> = {}; // key is item value, value is a number of occurrences
          const filteredDictionaryItems: DataBlockDictionaryItemDto[] = [];
          const itemGroups = groupBy(dictionaryItems, (item) =>
            item.value.toLowerCase(),
          );
          for (const key in itemGroups) {
            const items = itemGroups[key];
            if (items.length > 1) {
              duplicateValues[key] = items.length;
              // we import only last occurrence of item if file has duplicates
              filteredDictionaryItems.push(items[items.length - 1]);
              continue;
            }
            filteredDictionaryItems.push(items[0]);
          }

          const queryData = dictionaryItemsQuery.data?.data ?? [];
          const promises = filteredDictionaryItems.map((item) => {
            const existingItems = queryData.filter(
              (x) =>
                x.value.toLowerCase() === item.value.toLowerCase() ||
                (
                  i18nDictKey.t(x.value as any, {
                    nsSeparator: false,
                  }) as string
                ).toLowerCase() === item.value.toLowerCase(),
            );
            if (existingItems.length) {
              let existingItem = existingItems.find((x) => !x.isDeleted);
              if (!existingItem) {
                existingItem = existingItems[0];
              }
              // patch only if existing item is deleted or column values are different
              if (
                existingItem.isDeleted ||
                !isEqual(existingItem.values, item.values)
              ) {
                item.value = existingItem.value; // replace value with localization key if item is localized (to prevent loss of localization key)
                item.isDeleted = false;
                return QueryFactory.DataBlockDictionaryItemClient.patch(
                  existingItem.id,
                  item,
                );
              }
              return Promise.resolve();
            }
            return QueryFactory.DataBlockDictionaryItemClient.create(item);
          });
          await Promise.all(promises);
          if (Object.keys(duplicateValues).length) {
            await modals.showAlert({
              title: i18n.t('imported_file_contains_duplicates'),
              text: stringifyDuplicateValues(duplicateValues),
            });
          }
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        } finally {
          setIsLoading(false);
          setDictItemsQueryEnabled(true);
          await queryClient.invalidateQueries({
            queryKey:
              QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(
                dictionaryId,
              ),
          });
          // to allow choosing the same file again
          inputFileRef.current!.value = '';
        }
      },
      [currentDictionary, columns, referencedData, dictionaryItemsQuery.data],
    );

    const onRowClick = useEventCallback((e: RowClickEvent) => {
      const isEditable = !currentDictionary?.isReadOnly && !e.data.isDeleted;
      if (isEditable) {
        onEdit(e.data);
      }
    });

    const tableKey = useMemo(
      () =>
        `search=${queryParams.search}_count=${data.length}_updated-at=${dictionaryItemsQuery.dataUpdatedAt}_manual-ordering=${currentDictionary?.manualOrdering}${columns.map((x) => x.key).join('_')}`,
      [
        queryParams,
        data,
        dictionaryItemsQuery.dataUpdatedAt,
        currentDictionary,
        columns,
      ],
    );

    const dataGridElement = useMemo(() => {
      return (
        <DataGrid
          key={tableKey}
          dataSource={data}
          columns={columns}
          onRowClick={onRowClick}
          scrolling={{
            mode: 'virtual',
          }}
          enableScrollTop
          searchPanel={{
            visible: false,
            text:
              dictionaryItemsQuery.isLoading || isLoading
                ? ''
                : queryParams.search?.toLocaleLowerCase(),
          }}
        />
      );
    }, [
      tableKey,
      data,
      columns,
      isLoading,
      dictionaryItemsQuery.isLoading,
      queryParams.search,
    ]);

    return (
      <div
        className={styles.tableContainer}
        data-test-id="table-page-container"
      >
        {(currentDictionary?.columns ?? [])
          .filter((c) => c.referenceDictionaryId)
          .map((column) => (
            <ReferenceDictionaryColumnDataProvider
              key={column.id}
              column={column}
              onValuesReceived={(values) =>
                setReferencedData((prev) => ({
                  ...prev,
                  [column.title]: values,
                }))
              }
            />
          ))}

        <div className={styles.tableToolbar}>
          <TableTitle
            title={
              currentDictionary?.title
                ? i18nDictKey.t(currentDictionary.title as any)
                : ''
            }
            count={
              dictionaryItemsQuery.data?.data.filter((x) => !x.isDeleted)
                .length ?? undefined
            }
          />
          <div className={pageStyles.toolbar}>
            <div className={pageStyles.searchField}>
              {currentDictionary && !currentDictionary?.manualOrdering && (
                <SearchInput
                  placeholder={i18n.t('search_placeholder')}
                  bordered={true}
                  onSearchIconClick={onSearchUpdated}
                  onClear={onSearchUpdated}
                  onChange={(e) => onSearchUpdated(e.target.value)}
                  initialValue={queryParams.search}
                  allowAnySearch
                  blurOnEnter={false}
                />
              )}
            </div>
            {currentDictionary && !currentDictionary.isReadOnly && (
              <Button
                testId="plus-button"
                title={i18n.t('add_new_item', {
                  title: i18nDictKey.t(currentDictionary.title as any),
                })}
                iconComponent={<PlusIcon />}
                color={ButtonColor.Primary}
                onClick={onAddNewItem}
                className={pageStyles.createButton}
              />
            )}
          </div>
          {currentDictionary && !currentDictionary.isReadOnly && (
            <>
              <Button
                className={pageStyles.importButton}
                onClick={() => inputFileRef.current!.click()}
                title={i18n.t('import_button')}
                color={ButtonColor.Primary}
              />
              <input
                type="file"
                id="importExcelFile"
                ref={inputFileRef}
                className={'hidden'}
                onChange={importDictionaryFromFile}
              />
              <Button
                onClick={exportDictionary}
                title={i18n.t('export_button')}
                color={ButtonColor.Primary}
              />
            </>
          )}
          {currentDictionary &&
            !currentDictionary.isMandatory &&
            !currentDictionary.isReadOnly &&
            (claims.permissions.advancedDictionarySettings ||
              claims.permissions.dictionariesManagement) && (
              <IconButton
                color="grey"
                testId="settings-icon"
                iconComponent={
                  <SettingsIcon className={pageStyles.settingsIcon} />
                }
                onClick={() =>
                  globalForms.openConfigureCustomDictionary(
                    currentDictionary.id,
                  )
                }
                className={pageStyles.settingsButton}
              />
            )}
        </div>
        <div className={styles.patientsTable}>
          <Loading
            loading={
              isLoading ||
              dictionaryItemsQuery.isLoading ||
              currentDictionary === undefined
            }
            title={i18n.t('loading')}
          >
            {dataGridElement}
          </Loading>
        </div>
      </div>
    );
  },
);

export const ReferenceDictionaryColumnDataProvider: React.FC<{
  column: DataBlockDictionaryColumnDto;
  onValuesReceived: (data: DataBlockDictionaryItemDto[]) => void;
}> = (props) => {
  const column = props.column;
  const i18n = useTranslation(NS_DataBlockFields);
  const dictionaryQuery = QueryFactory.DataBlockDictionaryItemQuery.useGetQuery(
    { dictionaryId: column.referenceDictionaryId ?? 0, all: true },
    {
      enabled: !!column.referenceDictionaryId,
    },
  );

  useEffect(
    function callOnValuesReceivedWhenDataIsReceived() {
      if (dictionaryQuery.data) {
        props.onValuesReceived(
          dictionaryQuery.data.data
            .filter((x) => !x.isDeleted)
            .map((x) => ({
              id: x.id,
              value: i18n.t(x.value as any, { nsSeparator: false }),
              isDeleted: false,
              values: x.values,
              dictionaryId: x.dictionaryId,
              manualOrderPosition: x.manualOrderPosition,
            })),
        );
      }
    },
    [dictionaryQuery.data, column],
  );
  return null;
};

function stringifyDuplicateValues(duplicateValues: Record<string, number>) {
  const resultArray: string[] = [];
  for (const key in duplicateValues) {
    resultArray.push(`${key} (${duplicateValues[key]})`);
  }
  return resultArray.join(', ');
}
